/*
 * Steel Pony Farm — lazy-load home category rows on scroll.
 *
 * Each [data-spf-lazy-row] ships visible skeleton cards plus its REAL product
 * cards inside an inert <template>. Content inside a <template> isn't part of
 * the live document, so its web components never boot and it adds nothing to
 * the live DOM / layout / paint until we clone it in. When the shopper scrolls
 * near a row, we swap the skeletons for the real cards (which hydrate on
 * insertion). This keeps the one-page "scroll the whole store" feel while the
 * browser only holds a few rows of products live at once.
 *
 * Trigger: IntersectionObserver ONLY. It's passive — the browser reports
 * intersections during its normal rendering, so it never forces a layout.
 *
 * NOTE: a previous version also had a scroll/resize "safety net" that called
 * getBoundingClientRect() on each pending row. That FORCES a synchronous layout
 * on every scroll (made far worse by content-visibility on the rows), and a
 * DevTools profile pinned it as the add-while-scrolling jank. It's removed — IO
 * alone is correct and reliable.
 */
(function () {
  'use strict';

  // Mirrors realItems() in the "View all" unit below — kept local so each
  // block stays self-contained.
  function realItems(doc) {
    var tpl = doc.querySelector('template[data-spf-lazy-cards]');
    var scope = tpl ? tpl.content : doc;
    var nodes = scope.querySelectorAll('.resource-list__item');
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].classList.contains('spf-skel-item')) out.push(nodes[i]);
    }
    return out;
  }

  // The path part of a product link's href, query/hash stripped — a card's
  // main link is the variant URL (…/products/handle?variant=N), so two cards
  // for the same product only compare equal once the query is gone.
  // getAttribute, not .href: cards land here from DOMParser documents as well
  // as the live grid, and the raw attribute is the same string in both.
  function productPath(a) {
    return a.getAttribute('href').split('#')[0].split('?')[0];
  }

  // Swap real cards into the placeholder slots they belong in. Replacement is
  // 1:1 so indexes stay stable even though grid.children is a live list.
  //
  // DRIFT GUARD (2026-08-28): the seed renders at page load; the rest fetches
  // on scroll. If the collection's membership changes in between (a product
  // published or unpublished mid-browse), every later page boundary shifts by
  // one, so a fetched page re-serves a card the grid already holds and
  // overwrites its neighbour — seen live as "Zucchini (Large/XL) twice,
  // Zucchini (Medium) gone" while a pickling product was published
  // mid-browse. So: never place a product the grid already shows. The skipped
  // slot keeps its skeleton, which settle() clears. A row one card short
  // until reload is the acceptable failure; a duplicate that eats its
  // neighbour is not.
  function placeAt(grid, offset, items, total) {
    var slots = grid.children;
    var have = {};
    var links = grid.querySelectorAll('a[href*="/products/"]');
    for (var j = 0; j < links.length; j++) have[productPath(links[j])] = true;
    for (var i = 0; i < items.length; i++) {
      var idx = offset + i;
      if (idx >= total || idx >= slots.length) break;
      var link = items[i].querySelector('a[href*="/products/"]');
      var path = link ? productPath(link) : '';
      if (path && have[path]) continue;
      if (path) have[path] = true;
      grid.replaceChild(document.importNode(items[i], true), slots[idx]);
    }
  }

  // Any placeholder still standing once every request has come back means a
  // fetch failed. Clear the shimmer, but NEVER just let the row look shorter —
  // "products have been dropped" is the exact complaint the full-category row
  // was built to answer. Leave a way through to the whole category instead.
  function settle(grid, row, failed) {
    var leftovers = grid.querySelectorAll('.spf-skel-item');
    for (var i = 0; i < leftovers.length; i++) leftovers[i].remove();
    if (!failed || !leftovers.length) return;
    var url = row.dataset.spfCollectionUrl;
    if (!url || row.querySelector('[data-spf-rest-fallback]')) return;
    var wrap = document.createElement('div');
    wrap.className = 'spf-section-end spf-section-end--more';
    wrap.setAttribute('data-spf-rest-fallback', '');
    var a = document.createElement('a');
    a.className = 'spf-viewall';
    a.href = url;
    a.innerHTML =
      '<span class="spf-viewall__label">View all</span>' +
      '<span class="spf-viewall__chevron" aria-hidden="true"></span>';
    wrap.appendChild(a);
    row.appendChild(wrap);
  }

  // Fetch pages 2..restPages of this row's own section, in parallel. Each is a
  // small render (~85ms + ~6.3ms/card), and only rows the shopper actually
  // scrolls to ever ask — average scroll depth is 52%, so roughly half the
  // page's cards are never built at all.
  function fetchRest(row, grid, seed, total, restPages) {
    var sectionId = row.dataset.spfSectionId;
    if (!sectionId) return;
    var pending = restPages - 1;
    var failed = false;

    for (var page = 2; page <= restPages; page++) {
      (function (p) {
        fetch('/?section_id=' + encodeURIComponent(sectionId) + '&page=' + p, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            placeAt(grid, (p - 1) * seed, realItems(doc), total);
            // Fit any long product name into its fixed title box. Layout-neutral (the box
            // height is fixed), so this cannot move the page. See spf-card-title-fit.js.
            if (window.SPFFitTitles) window.SPFFitTitles(grid);
          })
          .catch(function () {
            failed = true;
          })
          .then(function () {
            if (--pending === 0) settle(grid, row, failed);
          });
      })(page);
    }
  }

  function hydrate(row) {
    if (!row || row.dataset.spfLazyDone === '1') return;
    var tpl = row.querySelector('template[data-spf-lazy-cards]');
    var grid = row.querySelector('.resource-list');
    if (!tpl || !grid) return;

    var seed = parseInt(row.dataset.spfSeed || '0', 10);
    var total = parseInt(row.dataset.spfTotal || '0', 10);
    var restPages = parseInt(row.dataset.spfRestPages || '1', 10);

    if (!seed || !total || restPages < 2) {
      // Unseeded row — the template still holds every card. Original path.
      // Cloning into the live document upgrades the custom elements.
      grid.replaceChildren(tpl.content.cloneNode(true));
      tpl.remove();
      row.dataset.spfLazyDone = '1';
      row.removeAttribute('data-spf-lazy-row');
      if (window.SPFFitTitles) window.SPFFitTitles(grid);
      return;
    }

    // Seeded row: the server sent only the first screen. Drop those cards in
    // and keep a skeleton standing in for every card still to come, so the
    // row occupies exactly the height it always did and nothing below it
    // moves as the rest land (this is what protects the 2026-07-28 CLS fix).
    var spare = grid.querySelector('.spf-skel-item');
    var placeholder = spare ? spare.cloneNode(true) : null;
    var frag = document.createDocumentFragment();
    frag.appendChild(tpl.content.cloneNode(true));
    var seeded = frag.childNodes.length ? frag.querySelectorAll('.resource-list__item').length : 0;
    if (placeholder) {
      for (var i = seeded; i < total; i++) frag.appendChild(placeholder.cloneNode(true));
    }
    grid.replaceChildren(frag);
    tpl.remove();
    row.dataset.spfLazyDone = '1';
    row.removeAttribute('data-spf-lazy-row');
    if (window.SPFFitTitles) window.SPFFitTitles(grid);

    fetchRest(row, grid, seed, total, restPages);
  }

  function init() {
    var rows = document.querySelectorAll('[data-spf-lazy-row]');
    if (!rows.length) return;

    // No IntersectionObserver (very old browser): just show everything rather
    // than leave rows stuck as skeletons.
    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < rows.length; i++) hydrate(rows[i]);
      return;
    }

    // Load a row when it's within ~one-and-a-bit screens of the viewport, so
    // its cards are ready before they scroll in (no visible pop-in). IO fires
    // immediately for any row already on screen at load.
    var obs = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            hydrate(entries[i].target);
            obs.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '1200px 0px' }
    );

    for (var j = 0; j < rows.length; j++) {
      // Let the "View all" unit below hydrate a row on demand, in case a click
      // somehow beats the observer (it shouldn't — rootMargin is 1200px — but
      // appending cards to a row that then hydrates would wipe them).
      rows[j].__spfHydrate = (function (r) {
        return function () {
          hydrate(r);
        };
      })(rows[j]);
      obs.observe(rows[j]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Skeleton styles (injected once; only visible while a row is unhydrated).
  //
  // THE SKELETON MUST BE THE SAME HEIGHT AS THE CARD IT IS STANDING IN FOR.
  // It wasn't: the placeholder was a bare 3/4 box (253x337 at desktop) while a real
  // product card measures 398 — a 1:1 image plus ~165px of title/price/Add button.
  // Every one of the ~17 home rows therefore shoved the page down ~64px the moment it
  // hydrated, which is where Clarity's CLS 0.5 came from (measured 2026-07-28).
  //
  // Reserve it in two parts, because the card scales in two ways: the IMAGE scales with
  // column width (aspect-ratio), while the title/price/button block is driven by font
  // size and does NOT scale with the column. A single aspect-ratio can't model both, so
  // the image box keeps an aspect-ratio and the text block is reserved as fixed padding.
  //
  // RE-MEASURED 2026-08-28, and the numbers moved a long way. The July reserve of 140/165
  // had gone stale exactly as the warning below predicted: the catalogue rename gave
  // products longer names, so titles that used to fit one line wrapped to two, and real
  // cards had drifted to 285/307/332/356px against a flat 286px reserve. Lighthouse put
  // the resulting jump at ~0.35 twice per load on the home page.
  //
  // product-card.liquid now pins every variable part of the card (three-line title box,
  // always-present price line, one button height, one-line badge row), so a card is a
  // SINGLE height at any given column width — verified across 156 live cards at 375px,
  // every one 356, and 91 cards at 1280px, every one 443-444.
  //
  // That also collapsed the two reserves into one: with the title box fixed at three
  // lines, nothing in the text block scales with the column any more, so the SAME 210px
  // is correct at both widths. The two aspect-ratios stay, because the image still scales.
  //   mobile  (166px column): image 146 (w/1.134) + text 210 = 356  == real card
  //   desktop (253px column): image 233 (w/1.086) + text 210 = 443  == real card (444)
  // If the card's typography, padding, title clamp or badge row is ever changed,
  // re-measure — a stale reserve silently reintroduces the layout shift. That has now
  // happened once; the comment was right and nobody re-measured.
  var css =
    '.spf-skel-item{padding-bottom:210px;box-sizing:border-box;}' +
    '.spf-skel-item .spf-skel-card{width:100%;aspect-ratio:1.134;border-radius:var(--card-corner-radius,8px);' +
    'background:#0000000d;background-image:linear-gradient(100deg,#0000 30%,#00000010 50%,#0000 70%);' +
    'background-size:200% 100%;animation:spf-skel 1.3s ease-in-out infinite;}' +
    '@media (min-width:750px){.spf-skel-item .spf-skel-card{aspect-ratio:1.086;}}' +
    '@keyframes spf-skel{0%{background-position:200% 0}100%{background-position:-200% 0}}' +
    '@media (prefers-reduced-motion:reduce){.spf-skel-item .spf-skel-card{animation:none}}';
  var style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
})();

/*
 * Steel Pony Farm — inline "View all": expand the rest of an overflow category
 * IN PLACE instead of navigating to the collection page.
 *
 * A category row renders up to 50 cards (Shopify's per-section paginate limit).
 * When a category has more, product-list.liquid emits a "View all" link carrying
 * the section id. We fetch the SAME section's next page via the Section Rendering
 * API (/?section_id=…&page=N) — byte-identical card markup — pull the real cards
 * out of its lazy <template>, and append them to the live grid. Custom elements
 * upgrade on insertion, so the new cards behave exactly like the originals. No
 * page change, no scroll reset. If anything fails, we fall back to the link's
 * href (the full collection page), so it degrades gracefully without JS too.
 */
(function () {
  'use strict';

  // Default page size. Seeded rows carry their real one on the link
  // (data-spf-page-size), because since Fix A the paginate page size is the
  // seed, not the 50-item cap.
  var PAGE_SIZE = 50;

  // Products revealed per "View all" click. Deliberately unchanged by Fix A —
  // the number of clicks a shopper needs must not go up.
  var CHUNK = 50;

  function fetchPage(sectionId, page) {
    return fetch('/?section_id=' + encodeURIComponent(sectionId) + '&page=' + page, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        return realItems(new DOMParser().parseFromString(html, 'text/html'));
      });
  }

  // Make sure a row's real cards are in the live grid (not still skeletons in an
  // inert <template>), so what we append won't be wiped by a later hydrate.
  function ensureHydrated(section) {
    var grid = section.querySelector('.resource-list');
    var lazyRow = section.querySelector('[data-spf-lazy-row]');
    if (grid && lazyRow) {
      // Prefer the observer's own hydrate: a seeded row needs its placeholders
      // and its fetch-the-rest pass, which the plain template swap below skips.
      if (typeof lazyRow.__spfHydrate === 'function') {
        lazyRow.__spfHydrate();
        return grid;
      }
      var tpl = lazyRow.querySelector('template[data-spf-lazy-cards]');
      if (tpl) {
        grid.replaceChildren(tpl.content.cloneNode(true));
        tpl.remove();
      }
      lazyRow.removeAttribute('data-spf-lazy-row');
      lazyRow.dataset.spfLazyDone = '1';
    }
    return grid;
  }

  // Pull the real (non-skeleton) product cards out of a fetched section, whether
  // it came back lazy (cards inside a <template>) or eager (cards in the grid).
  function realItems(doc) {
    var tpl = doc.querySelector('template[data-spf-lazy-cards]');
    var scope = tpl ? tpl.content : doc;
    var nodes = scope.querySelectorAll('.resource-list__item');
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].classList.contains('spf-skel-item')) out.push(nodes[i]);
    }
    return out;
  }

  // All loaded: collapse the "View all" row back to a plain hairline separator.
  function finish(link) {
    var wrap = link.closest('.spf-section-end');
    if (wrap) {
      wrap.classList.remove('spf-section-end--more');
      wrap.replaceChildren();
      wrap.setAttribute('aria-hidden', 'true');
    }
  }

  function expand(link) {
    if (link.dataset.spfLoading === '1') return;
    var section = link.closest('[data-testid="product-list"]');
    var sectionId = link.dataset.spfSectionId;
    if (!section || !sectionId) {
      window.location.href = link.href;
      return;
    }
    var grid = ensureHydrated(section);
    if (!grid) {
      window.location.href = link.href;
      return;
    }

    link.dataset.spfLoading = '1';
    link.setAttribute('aria-busy', 'true');
    var labelEl = link.querySelector('.spf-viewall__label');
    var prevLabel = labelEl ? labelEl.textContent : '';
    if (labelEl) labelEl.textContent = 'Loading…';

    var page = parseInt(link.dataset.spfNextPage || '2', 10);
    var pageSize = parseInt(link.dataset.spfPageSize || '', 10) || PAGE_SIZE;

    // ONE CLICK STILL REVEALS ~50 PRODUCTS. Fix A shrank the paginate page size
    // to the seed, which would otherwise have turned a single "View all" into
    // five clicks — precisely the kind of friction this row exists to avoid.
    // So fan out enough pages to make up a full chunk and fetch them together.
    var pagesPerClick = Math.max(1, Math.ceil(CHUNK / pageSize));

    var jobs = [];
    for (var i = 0; i < pagesPerClick; i++) {
      jobs.push(fetchPage(sectionId, page + i));
    }

    Promise.all(jobs)
      .then(function (results) {
        // results keeps request order, so cards stay in catalogue order even
        // though the requests came back in whatever order they finished.
        var frag = document.createDocumentFragment();
        var added = 0;
        var exhausted = false;
        for (var i = 0; i < results.length; i++) {
          var items = results[i];
          for (var j = 0; j < items.length; j++) {
            frag.appendChild(document.importNode(items[j], true));
          }
          added += items.length;
          if (items.length < pageSize) {
            exhausted = true; // a short page means there is nothing after it
            break;
          }
        }
        if (added) grid.appendChild(frag);
        link.dataset.spfNextPage = String(page + pagesPerClick);

        if (exhausted || !added) {
          finish(link); // nothing more to load
        } else {
          link.dataset.spfLoading = '0';
          link.removeAttribute('aria-busy');
          if (labelEl) labelEl.textContent = prevLabel;
        }
      })
      .catch(function () {
        // Graceful fallback: open the full collection page.
        window.location.href = link.href;
      });
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-spf-loadmore]');
    if (!link) return;
    e.preventDefault();
    expand(link);
  });
})();
